import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Sparkles, ArrowRight, Eye, EyeOff } from "lucide-react";

const BG = "https://static.prod-images.emergentagent.com/jobs/c36a8fad-3db1-43ac-a499-952035e476fa/images/6caba51d7492872234ec31fd4c9f631cf293d605214010474c72dd10123ee18e.png";

export default function Login() {
  const { user, login, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  if (loading) return null;
  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/student"} replace />;

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email.trim(), password);
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error || "Login failed");
      return;
    }
    navigate(res.user.role === "admin" ? "/admin" : "/student", { replace: true });
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-cream" data-testid="login-page">
      <div className="flex flex-col px-6 sm:px-10 lg:px-16 py-8 lg:py-12">
        <Link to="/" className="flex items-center gap-2 group">
          <div className="h-9 w-9 rounded-md bg-terracotta flex items-center justify-center text-white">
            <Sparkles className="h-4 w-4" />
          </div>
          <div>
            <div className="font-heading text-2xl text-deep leading-none">Cozy Heaven</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#A67B5B] mt-1">Resident & Admin Portal</div>
          </div>
        </Link>

        <div className="flex-1 flex items-center">
          <div className="w-full max-w-md mx-auto">
            <div className="text-xs font-semibold uppercase tracking-[0.25em] text-[#A67B5B] mb-3">welcome back</div>
            <h1 className="font-heading text-4xl sm:text-5xl text-deep leading-tight tracking-tight">
              Step inside,<br/><span className="italic text-[#C86F58]">it's been quiet without you.</span>
            </h1>
            <p className="text-warm mt-4 text-sm">Sign in with your portal credentials.</p>

            <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="login-form">
              <div>
                <label className="text-sm font-medium text-deep mb-1.5 block">Email</label>
                <input
                  type="email"
                  required
                  autoFocus
                  data-testid="login-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@cozyheaven.com"
                  className="w-full px-3.5 py-2.5 rounded-md border border-warm bg-white focus:outline-none focus:ring-2 focus:ring-[#E8C2B8] focus:border-[#C86F58] text-deep placeholder:text-[#A8A39D]"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-deep mb-1.5 block">Password</label>
                <div className="relative">
                  <input
                    type={show ? "text" : "password"}
                    required
                    data-testid="login-password-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-3.5 py-2.5 pr-10 rounded-md border border-warm bg-white focus:outline-none focus:ring-2 focus:ring-[#E8C2B8] focus:border-[#C86F58] text-deep"
                  />
                  <button
                    type="button"
                    onClick={() => setShow((s) => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-warm hover:text-deep p-1"
                    aria-label="toggle visibility"
                    data-testid="toggle-password-visibility"
                  >
                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="text-sm text-[#8C3F3F] bg-[#F5D8D8] border border-[#E8C2B8] rounded-md px-3 py-2" data-testid="login-error">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                data-testid="login-submit-button"
                className="w-full bg-terracotta text-white py-3 rounded-md font-medium hover:scale-[1.01] active:scale-[0.98] transition-transform inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? "Signing you in…" : <>Continue <ArrowRight className="h-4 w-4" /></>}
              </button>
            </form>

            <div className="mt-8 bg-[#F4F1EA] border border-warm rounded-md p-4 text-xs text-warm leading-relaxed">
              <div className="text-deep font-medium mb-1">Demo credentials</div>
              Admin · <span className="font-mono">admin@cozyheaven.com</span> / <span className="font-mono">Cozy@Admin2026</span>
            </div>
          </div>
        </div>

        <div className="text-xs text-warm">© {new Date().getFullYear()} Cozy Heaven</div>
      </div>

      <div className="hidden lg:block relative">
        <img src={BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#2C2A29]/40 via-transparent to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="font-heading text-3xl xl:text-4xl leading-tight max-w-md">
            "Cozy Heaven isn't just a PG — it's a soft landing in a new city."
          </div>
          <div className="text-xs mt-3 uppercase tracking-[0.25em]">— Our residents</div>
        </div>
      </div>
    </div>
  );
}
